import AddIcon from "@mui/icons-material/AddRounded";
import { Button } from "@mui/material";
import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import type { DataImportOption } from "../../../api/types/dataImportTypes";
import type { ObjectClassDefinition, ObjectPermissions } from "../../../api/types/objectTypes";
import useClientContext from "../../../context/useClientContext";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import EntitiesList from "./EntitiesList";

interface Props {
  objectClassDefinition: ObjectClassDefinition;
  objectPermissions: ObjectPermissions;
  dataImportOptions: DataImportOption[];
  objectDetailsPathPattern: string;
  objectsTitle?: string;
  showLogos?: boolean;
  allowCreate?: boolean;
}

const EntitiesListPageContainer = ({
  objectClassDefinition,
  objectPermissions,
  dataImportOptions,
  objectDetailsPathPattern,
  objectsTitle,
  showLogos,
  allowCreate,
}: Props) => {
  usePageTitle(objectsTitle || objectClassDefinition.objectName);

  const { clientCode, hasAnyPermission } = useClientContext();
  const navigate = useNavigate();

  const getObjectDetailsPath = useCallback(
    (id: string) => generatePath(objectDetailsPathPattern, { clientCode, type: objectClassDefinition.objectType, id }),
    [clientCode, objectClassDefinition.objectType, objectDetailsPathPattern]
  );

  const handleCreateObject = () => {
    const pathToNewObject = generatePath(objectDetailsPathPattern, {
      clientCode,
      type: objectClassDefinition.objectType,
      id: "new",
    });

    navigate(pathToNewObject);
  };

  const hasEditPermission = hasAnyPermission(objectPermissions.objectDataWritePermissions);

  const showCreationButton = !!allowCreate && hasEditPermission;

  return (
    <>
      <GeneralPageHeader title={objectsTitle || objectClassDefinition.objectName}>
        {showCreationButton && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateObject}>
            Create New
          </Button>
        )}
      </GeneralPageHeader>

      <EntitiesList
        objectClassDefinition={objectClassDefinition}
        dataImportOptions={dataImportOptions}
        showLogos={showLogos}
        getDetailsPath={getObjectDetailsPath}
      />
    </>
  );
};

export default EntitiesListPageContainer;
